import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/skip-link?tab=designer).
 *
 * @slot default - Slot para inserir os itens de navegação rápida (br-skiplink-item).
 *
 * @part container - Parte para o contêiner principal do componente skip-link, usada para estilização via ::part().
 */
@Component({
  tag: 'br-skip-link',
  styleUrl: 'skip-link.scss',
  shadow: true,
})
export class SkipLink {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrSkipLinkElement

  @AttachInternals() elementInternals: ElementInternals

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId()

  /**
   * Define se o skip link apresenta um ou múltiplos itens visíveis.
   * - 'simple': Exibe apenas um item por vez.
   * - 'compound': Exibe uma lista com múltiplos itens.
   */
  @Prop({ reflect: true }) readonly variant: 'simple' | 'compound' = 'compound'

  /**
   * Define a posição do skip link na tela.
   * - 'top-left': Posicionado no canto superior esquerdo.
   * - 'top-center': Posicionado no centro superior.
   * - 'top-right': Posicionado no canto superior direito.
   */
  @Prop({ reflect: true }) readonly position: 'top-left' | 'top-center' | 'top-right' = 'top-left'

  /**
   * Define o z-index do componente.
   * Controla a ordem de empilhamento do componente na página.
   */
  @Prop({ reflect: true }) readonly zIndex: number | null = null

  /**
   * Exibe a contagem de itens no formato (1/4), (2/4), etc.
   * Quando `true`, cada item mostrará sua posição relativa ao total de itens.
   * Por padrão, é `true` para o modo simple e `false` para o modo compound.
   */
  @Prop({ reflect: true }) readonly showItemCount?: boolean

  /**
   * Estado interno que controla a visibilidade do componente.
   */
  @State() private isVisible: boolean = false

  /**
   * Define se o componente está em modo tela pequena
   */
  @State() private isSmallScreen: boolean = false

  /**
   * Define se o componente precisa mudar para modo simples por falta de espaço
   */
  @State() private needsSimpleMode: boolean = false

  /**
   * Largura do container pai
   */
  @State() private containerWidth: number = 0

  /**
   * ResizeObserver para monitorar mudanças no tamanho do container
   */
  private resizeObserver: ResizeObserver

  /**
   * Evento emitido quando um item do skiplink é ativado.
   * O evento contém informações sobre o item acionado e o elemento target.
   */
  @Event() brSkiplinkNavigation: EventEmitter<{ itemId: string; target: string }>

  /**
   * Evento emitido quando o skiplink se torna visível.
   */
  @Event() brDidShow: EventEmitter<void>

  /**
   * Evento emitido quando o skiplink se torna oculto.
   */
  @Event() brDidHide: EventEmitter<void>

  @Watch('zIndex')
  updateStyles() {
    if (this.zIndex !== null && this.zIndex !== undefined) {
      this.el.style.zIndex = `${this.zIndex}`
      this.el.style.setProperty('--skiplink-layer', `${this.zIndex}`)
    }
  }

  /**
   * Observa mudanças nos estados que afetam o modo efetivo (simple/compound)
   * e atualiza as contagens dos itens quando necessário.
   */
  @Watch('needsSimpleMode')
  @Watch('isSmallScreen')
  watchModeChanges() {
    this.updateItemCounts()
  }

  /**
   * Listener para eventos de navegação dos itens filhos.
   */
  @Listen('brSkiplinkItemClick')
  handleItemNavigation(event: CustomEvent<{ itemId: string; target: string }>) {
    this.brSkiplinkNavigation.emit(event.detail)
    this.hide()
  }

  /**
   * Listener para evento de foco no componente.
   */
  @Listen('focusin')
  handleFocusIn() {
    if (!this.isVisible) {
      this.show()
    }
  }

  /**
   * Listener para evento de perda de foco no componente.
   */
  @Listen('focusout')
  handleFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement | null
    if (relatedTarget === null || !this.el.contains(relatedTarget)) {
      this.hide()
    }
  }

  /**
   * Listener para a tecla Escape.
   */
  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isVisible) {
      event.preventDefault()
      this.hide()
      ;(document.activeElement as HTMLElement)?.blur()
      return
    }
  }

  connectedCallback() {
    document.addEventListener('keydown', this.globalKeyHandler)

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.updateContainerSize)
      const parentElement = this.el.parentElement
      if (parentElement !== null) {
        this.resizeObserver.observe(parentElement)
      }
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.globalKeyHandler)

    if (this.resizeObserver !== undefined && this.resizeObserver !== null) {
      this.resizeObserver.disconnect()
    }
  }

  componentWillLoad() {
    const parentElement = this.el.parentElement
    if (parentElement !== null) {
      this.containerWidth = parentElement.clientWidth
    }
  }

  componentDidLoad() {
    this.updateItemCounts()
    this.calculateItemsWidth()
  }

  /**
   * Manipulador global para atalhos de teclado Alt+[número].
   */
  private globalKeyHandler = (event: KeyboardEvent) => {
    if (event.altKey && /^[1-9]$/.test(event.key)) {
      const items = this.el.querySelectorAll('br-skiplink-item') as NodeListOf<HTMLBrSkiplinkItemElement>
      const targetItem = Array.from(items).find((item) => {
        const keyNumber = item.getAttribute('key-number')
        return keyNumber === event.key
      })

      if (targetItem && typeof targetItem.navigateToTarget === 'function') {
        event.preventDefault()
        targetItem.navigateToTarget()
        this.show()
      }
    }
  }

  /**
   * Calcula a largura total dos itens e atualiza os estados do componente
   */
  private updateContainerSize = (entries: ResizeObserverEntry[]) => {
    for (const entry of entries) {
      const width = entry.contentRect.width
      this.containerWidth = width
      this.isSmallScreen = width < 570

      if (!this.isSmallScreen) {
        this.calculateItemsWidth()
      } else {
        this.needsSimpleMode = false
      }
    }
  }

  /**
   * Calcula a largura total dos itens do skip-link e determina se deve usar modo simple ou compound.
   */
  private calculateItemsWidth() {
    requestAnimationFrame(() => {
      const items = this.el.querySelectorAll('br-skiplink-item') as NodeListOf<HTMLBrSkiplinkItemElement>

      if (items.length === 0 || this.isSmallScreen) return
      if (items.length > 4) {
        this.needsSimpleMode = true
        return
      }

      this.needsSimpleMode = false
      const totalWidth = this.sumItemsWidth(items)

      const availableWidth = this.calculateAvailableWidth()

      if (totalWidth > availableWidth) {
        this.needsSimpleMode = true
      }
    })
  }

  /**
   * Soma a largura de todos os itens do skip-link
   */
  private sumItemsWidth(items: NodeListOf<HTMLBrSkiplinkItemElement>): number {
    let total = 0

    items.forEach((item) => {
      const brItem = item.shadowRoot?.querySelector('br-item') as HTMLElement | null
      if (brItem === null) return

      const width = brItem.offsetWidth || brItem.scrollWidth || brItem.getBoundingClientRect().width
      total += width
    })

    return total
  }

  /**
   * Calcula a largura disponível descontando margens e margem de segurança
   */
  private calculateAvailableWidth(): number {
    if (this.containerWidth === 0) return 0

    // Obtém o valor da margem definida no CSS
    const computedStyle = getComputedStyle(this.el)
    const spacingValue = computedStyle.getPropertyValue('--spacing-scale-3x').trim()
    const margin = parseFloat(spacingValue) || 24

    // Margem de segurança para evitar quebras visuais
    const safetyMargin = 20

    // Desconta: margem esquerda + margem direita + margem de segurança
    return this.containerWidth - margin * 2 - safetyMargin
  }

  /**
   * Atualiza os atributos de contagem nos itens filhos.
   * Adiciona data-item-index e data-item-total em cada br-skiplink-item.
   * A contagem é exibida quando o componente está em modo simple (incluindo modo responsivo).
   */
  private updateItemCounts() {
    const effectiveVariant = this.isSmallScreen || this.needsSimpleMode ? 'simple' : this.variant
    const shouldShowCount = this.showItemCount !== undefined ? this.showItemCount : effectiveVariant === 'simple'
    const isCompoundMode = effectiveVariant === 'compound'

    const items = this.el.querySelectorAll('br-skiplink-item') as NodeListOf<HTMLBrSkiplinkItemElement>

    items.forEach((item) => {
      if (isCompoundMode) {
        item.classList.add('compound-mode')
      } else {
        item.classList.remove('compound-mode')
      }
    })

    if (!shouldShowCount) {
      items.forEach((item) => {
        item.removeAttribute('data-item-index')
        item.removeAttribute('data-item-total')
      })
      return
    }

    const total = items.length
    items.forEach((item, index) => {
      item.setAttribute('data-item-index', (index + 1).toString())
      item.setAttribute('data-item-total', total.toString())
    })
  }

  /**
   * Oculta o componente programaticamente.
   */
  @Method()
  async hide(): Promise<void> {
    const focusedElement = this.el.querySelector(':focus')
    if (focusedElement instanceof HTMLElement) {
      focusedElement.blur()
    }
    this.isVisible = false
    this.brDidHide.emit()
    return Promise.resolve()
  }

  /**
   * Exibe o componente programaticamente.
   */
  @Method()
  async show(): Promise<void> {
    this.isVisible = true

    await new Promise((resolve) => requestAnimationFrame(resolve))

    const firstItem = this.el.querySelector('br-skiplink-item') as HTMLBrSkiplinkItemElement | null
    if (firstItem !== null) {
      if (typeof firstItem.itemFocus === 'function') {
        await firstItem.itemFocus()
      } else {
        const focusableElement = firstItem.shadowRoot?.querySelector('a, button') as HTMLElement | null
        if (focusableElement !== null) {
          focusableElement.focus()
        }
      }
    }

    this.brDidShow.emit()
    return Promise.resolve()
  }

  private getEffectivePosition(): 'top-left' | 'top-center' | 'top-right' {
    if (this.isSmallScreen) {
      return 'top-left'
    }
    return this.position
  }

  private getCssClassMap(): CssClassMap {
    const effectiveVariant = this.isSmallScreen || this.needsSimpleMode ? 'simple' : this.variant
    const effectivePosition = this.getEffectivePosition()

    return {
      'br-skiplink': true,
      full: effectiveVariant === 'compound',
      [effectivePosition]: true,
      visible: this.isVisible,
    }
  }

  render() {
    return (
      <Host>
        <nav part="container" class={this.getCssClassMap()} id={this.customId}>
          <br-list>
            <slot></slot>
          </br-list>
        </nav>
      </Host>
    )
  }
}
